import type { Translations } from "vue3-gettext";
import { createGettext } from "vue3-gettext";
import { getEnumKeyByValue } from "@/utilities/enum";
import { CurrencyId } from "@/services/currencies/currenciesTypes";
import { useAppStore } from "@/composables/appStore";
import { z } from "zod";

export enum LanguageCode {
  Cs = "cs",
  Sk = "sk",
}

export enum LanguageId {
  Cs = 1,
  Sk = 2,
}
export const LanguageId_Schema = z.enum(LanguageId);

export enum CountryCode {
  Cz = "cz",
  Sk = "sk",
}

export const LanguageCode2NameMap: Record<LanguageCode, string> = {
  [LanguageCode.Cs]: "Čeština",
  [LanguageCode.Sk]: "Slovenčina",
};

export const LanguageCode2LanguageIdMap: Record<LanguageCode, LanguageId> = {
  [LanguageCode.Cs]: LanguageId.Cs,
  [LanguageCode.Sk]: LanguageId.Sk,
};

export const LanguageId2LanguageCodeMap: Record<LanguageId, LanguageCode> = {
  [LanguageId.Cs]: LanguageCode.Cs,
  [LanguageId.Sk]: LanguageCode.Sk,
};

export const LanguageId2CountryCodeMap: Record<LanguageId, CountryCode> = {
  [LanguageId.Cs]: CountryCode.Cz,
  [LanguageId.Sk]: CountryCode.Sk,
};

/**
 * See https://github.com/HatScripts/circle-flags for available codes.
 */
export const LanguageCode2FlagMap: Record<LanguageCode, string> = {
  [LanguageCode.Cs]: "cz",
  [LanguageCode.Sk]: "sk",
};

const NavigatorLanguage2CountryCodeMap: Record<string, CountryCode> = {
  ["cs"]: CountryCode.Cz,
  ["cs-CZ"]: CountryCode.Cz,
  ["sk"]: CountryCode.Sk,
  ["sk-SK"]: CountryCode.Sk,
};

export const CountryCode2LanguageCodeMap: Record<CountryCode, LanguageCode> = {
  [CountryCode.Cz]: LanguageCode.Cs,
  [CountryCode.Sk]: LanguageCode.Sk,
};

export const LanguageCode2DefaultCurrencyIdMap: Record<LanguageCode, CurrencyId> = {
  [LanguageCode.Cs]: CurrencyId.Czk,
  [LanguageCode.Sk]: CurrencyId.Eur,
};

export const FallbackLanguageCode = LanguageCode.Cs;
export const FallbackCountryCode = CountryCode.Cz;

export const gettext = createGettext({
  availableLanguages: LanguageCode2NameMap,
  defaultLanguage: FallbackLanguageCode,
  translations: {},
});

export const setCountry = async (country: CountryCode): Promise<void> => {
  const language = CountryCode2LanguageCodeMap[country];
  await setLanguage(language);

  useAppStore().setCountryCode(country);
};

let translationPromise: Promise<Translations> | undefined = undefined;
export const loadTranslations = async (language: LanguageCode) => {
  if (translationPromise === undefined) {
    translationPromise = import(`@/locales/${language}.json`);
  }
  return translationPromise;
};

export const setLanguage = async (language: LanguageCode): Promise<void> => {
  if (!gettext.translations[language]) {
    translationPromise = undefined;
    const messages = await loadTranslations(language);
    gettext.translations = messages;
  }
  gettext.current = language;
  // @nuxt-rework: replace with useHead({ htmlAttrs: { lang: language } })
  if (typeof document !== "undefined") document.documentElement.lang = language;

  const languageId = LanguageCode2LanguageIdMap[language];
  useAppStore().setLanguageId(languageId);
};

export const getLanguageObject = (languageCode: string) => {
  const code = getEnumKeyByValue(languageCode, LanguageCode);
  const flag = LanguageCode2FlagMap[code];
  const label = LanguageCode2NameMap[code];
  return { code, flag, label };
};

export const autodetectCountryCode = (): CountryCode => {
  // @nuxt-rework: replace with useRequestHeaders()['accept-language'] parsed on server
  const navigatorLang = typeof navigator !== "undefined" ? navigator.language : undefined;
  const countryCode = navigatorLang ? NavigatorLanguage2CountryCodeMap[navigatorLang] : undefined;
  return countryCode ?? FallbackCountryCode;
};
